import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, XCircle, Lock } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useFlags } from "@/hooks/useFlags";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Partner() {
  const { t } = useTranslation();
  const v3 = useFlags().v3_enabled;
  const [form, setForm] = useState({ code: "", pin: "" });
  const [result, setResult] = useState(null);
  const [preq, setPreq] = useState({ name: "", type: "activite", email: "", phone: "", island: "", description: "" });

  const submitPreq = async () => {
    if (!preq.name || !preq.email) { toast.error(t("partnerReq.required")); return; }
    try {
      await api.post("/partner-requests", preq);
      toast.success(t("partnerReq.success"));
      setPreq({ name: "", type: "activite", email: "", phone: "", island: "", description: "" });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const call = async (action) => {
    try {
      const { data } = await api.post("/partner/validate", { code: form.code, pin: form.pin, action });
      setResult(data);
      if (action === "use") toast.success(t("partner.validated"));
    } catch (e) { setResult(null); toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="section-sand min-h-[80vh] flex flex-col items-center justify-center gap-6 py-12 px-4">
      <div className="bg-white rounded-3xl shadow-card border border-border p-8 max-w-md w-full">
        <h1 className="font-heading font-extrabold text-2xl text-ink flex items-center gap-2 mb-2"><ShieldCheck className="w-7 h-7 text-canard" /> {t("partner.title")}</h1>
        <p className="text-sm text-muted2 font-body mb-6">{t("partner.desc")}</p>
        {!v3 ? (
          <div data-testid="partner-disabled" className="text-center py-8 text-muted2 font-body"><Lock className="w-8 h-8 text-canard mx-auto mb-3" />{t("partner.disabled")}</div>
        ) : (
        <>
        <div className="space-y-3">
          <Input data-testid="partner-code" placeholder={t("partner.code")} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="h-12 rounded-xl bg-sand" />
          <Input data-testid="partner-pin" type="password" placeholder={t("partner.pin")} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} className="h-12 rounded-xl bg-sand" />
          <div className="flex gap-2">
            <Button data-testid="partner-check" onClick={() => call("check")} variant="outline" className="flex-1 rounded-full font-bold">{t("partner.check")}</Button>
            <Button data-testid="partner-use" onClick={() => call("use")} className="flex-1 rounded-full bg-canard hover:bg-canard-dark font-bold">{t("partner.use")}</Button>
          </div>
        </div>
        {result && (
          <div data-testid="partner-result" className="mt-6 p-4 rounded-2xl bg-sand">
            <div className="flex items-center gap-2 mb-2">
              {result.status === "active" ? <CheckCircle2 className="w-5 h-5 text-emeraude" /> : <XCircle className="w-5 h-5 text-yellow-600" />}
              <span className="font-bold text-ink">{result.status === "active" ? t("partner.active") : t("partner.used")}</span>
            </div>
            <p className="text-sm text-ink font-semibold">{result.reward_title}</p>
            <p className="text-xs text-muted2">{result.partner} · {result.points_cost} pts</p>
          </div>
        )}
        </>
        )}
      </div>
      {v3 && (
        <div data-testid="partner-request-card" className="bg-white rounded-3xl shadow-card border border-border p-8 max-w-md w-full">
          <h2 className="font-heading font-extrabold text-xl text-ink mb-1">{t("partnerReq.title")}</h2>
          <p className="text-sm text-muted2 font-body mb-5">{t("partnerReq.sub")}</p>
          <div className="space-y-3">
            <Input data-testid="preq-name" placeholder={t("partnerReq.name")} value={preq.name} onChange={(e) => setPreq({ ...preq, name: e.target.value })} className="h-11 rounded-xl bg-sand" />
            <select data-testid="preq-type" value={preq.type} onChange={(e) => setPreq({ ...preq, type: e.target.value })} className="h-11 w-full rounded-xl bg-sand px-3 text-sm">
              <option value="activite">{t("partnerReq.tActivity")}</option>
              <option value="restaurant">{t("partnerReq.tRestaurant")}</option>
              <option value="lieu">{t("partnerReq.tPlace")}</option>
              <option value="decouverte">{t("partnerReq.tDiscovery")}</option>
            </select>
            <Input data-testid="preq-email" type="email" placeholder={t("partnerReq.email")} value={preq.email} onChange={(e) => setPreq({ ...preq, email: e.target.value })} className="h-11 rounded-xl bg-sand" />
            <Input data-testid="preq-phone" placeholder={t("partnerReq.phone")} value={preq.phone} onChange={(e) => setPreq({ ...preq, phone: e.target.value })} className="h-11 rounded-xl bg-sand" />
            <Input data-testid="preq-island" placeholder={t("partnerReq.island")} value={preq.island} onChange={(e) => setPreq({ ...preq, island: e.target.value })} className="h-11 rounded-xl bg-sand" />
            <textarea data-testid="preq-description" placeholder={t("partnerReq.description")} value={preq.description} onChange={(e) => setPreq({ ...preq, description: e.target.value })} rows={3} className="w-full rounded-xl bg-sand p-3 text-sm" />
            <Button data-testid="preq-submit" onClick={submitPreq} className="w-full rounded-full bg-canard hover:bg-canard-dark font-bold h-11">{t("partnerReq.submit")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
